// src/pages/PostNeed.jsx
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../lib/AuthContext";

const INDUSTRIES = ["AI & Automation", "Sustainability", "Supply Chain", "FinTech", "Healthcare", "Logistics", "Retail Tech", "Cybersecurity", "Other"];
const BUDGETS = ["<$50k", "$50k–$150k", "$150k–$500k", "$500k+", "Negotiable"];
const TIMELINES = ["<1 month", "1–3 months", "3–6 months", "6+ months"];
const STAGES = ["Any stage", "Pre-seed", "Seed", "Series A", "Series B+"];
const AVATAR_COLORS = [
  { bg: "#E6F1FB", text: "#185FA5" }, { bg: "#FAEEDA", text: "#633806" },
  { bg: "#FAECE7", text: "#712B13" }, { bg: "#E1F5EE", text: "#085041" },
  { bg: "#EEEDFE", text: "#3C3489" }, { bg: "#FBEAF0", text: "#72243E" }
];

export default function PostNeed() {
  const location = useLocation();
  const prefill = location.state?.prefill;
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: prefill?.title || "",
    description: prefill?.description || "",
    industry: prefill?.industry || "",
    budget: prefill?.budget || "",
    timeline: prefill?.timeline || "",
    startupStage: "Any stage",
    additionalContext: prefill?.additionalContext || "",
    tagInput: "",
    tags: prefill?.tags || []
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  function set(field) { return e => setForm(f => ({ ...f, [field]: e.target.value })); }

  function addTag() {
    const t = form.tagInput.trim();
    if (t && !form.tags.includes(t) && form.tags.length < 8) {
      setForm(f => ({ ...f, tags: [...f.tags, t], tagInput: "" }));
    }
  }
  function removeTag(tag) { setForm(f => ({ ...f, tags: f.tags.filter(t => t !== tag) })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const colorPair = AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];
      const docRef = await addDoc(collection(db, "needs"), {
        title: form.title,
        description: form.description,
        industry: form.industry,
        budget: form.budget,
        timeline: form.timeline,
        startupStage: form.startupStage,
        additionalContext: form.additionalContext,
        tags: form.tags,
        corpId: currentUser.uid,
        corpName: userProfile?.orgName || userProfile?.displayName,
        corpAvatar: (userProfile?.orgName || userProfile?.displayName || "??").slice(0, 2).toUpperCase(),
        avatarColor: colorPair.bg,
        avatarTextColor: colorPair.text,
        status: "new",
        pitchCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      navigate("/my-needs");
    } catch (err) {
      setError("Failed to post challenge. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="app-shell app-shell--form">
      <div className="app-form-canvas">
      <header className="app-page-header">
        <div className="app-page-header-copy">
          <h1 className="app-page-title">Post a challenge</h1>
          <p className="app-page-subtitle">Describe your challenge clearly — the more detail you provide, the better quality solutions you'll receive.</p>
        </div>
      </header>

      {prefill && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10, padding: "12px 16px",
          background: "rgba(37,99,212,0.08)", border: "1px solid rgba(37,99,212,0.2)",
          borderRadius: "var(--radius)", marginBottom: 20
        }}>
          <span style={{ fontSize: 18 }}>🧠</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>Pre-filled by AI Challenge Framer</div>
            <div style={{ fontSize: 12, color: "var(--text-3)" }}>Review and edit the fields below before posting</div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ padding: "24px", display: "flex", flexDirection: "column", gap: 18 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, borderBottom: "1px solid var(--border)", paddingBottom: 12 }}>About the challenge</h3>

          <div className="form-group">
            <label className="form-label">Challenge title <span style={{ color: "#d32f2f" }}>*</span></label>
            <input className="input" value={form.title} onChange={set("title")} placeholder="e.g. Real-time carbon tracking for manufacturing lines" required maxLength={120} />
            <div style={{ fontSize: 11, color: "var(--text-3)", textAlign: "right" }}>{form.title.length}/120</div>
          </div>

          <div className="form-group">
            <label className="form-label">Full description <span style={{ color: "#d32f2f" }}>*</span></label>
            <textarea className="textarea" style={{ minHeight: 130 }} value={form.description} onChange={set("description")} placeholder="Explain the challenge in detail. What have you tried? What does success look like? Who will use this?" required />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Industry <span style={{ color: "#d32f2f" }}>*</span></label>
              <select className="select" value={form.industry} onChange={set("industry")} required>
                <option value="">Select industry</option>
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Budget range <span style={{ color: "#d32f2f" }}>*</span></label>
              <select className="select" value={form.budget} onChange={set("budget")} required>
                <option value="">Select budget</option>
                {BUDGETS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Timeline needed <span style={{ color: "#d32f2f" }}>*</span></label>
              <select className="select" value={form.timeline} onChange={set("timeline")} required>
                <option value="">Select timeline</option>
                {TIMELINES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Preferred startup stage</label>
              <select className="select" value={form.startupStage} onChange={set("startupStage")}>
                {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Tags / keywords</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input className="input" value={form.tagInput} onChange={set("tagInput")}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Type a tag and press Enter" />
              <button type="button" className="btn btn-outline" onClick={addTag} style={{ whiteSpace: "nowrap" }}>Add tag</button>
            </div>
            {form.tags.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {form.tags.map(t => (
                  <span key={t} style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "3px 10px", borderRadius: 99, background: "var(--green-light)", color: "var(--green-text)", fontSize: 12
                  }}>
                    {t}
                    <button type="button" onClick={() => removeTag(t)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--green-text)", lineHeight: 1, padding: "0 2px", fontSize: 14 }}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Additional context <span style={{ fontSize: 11, color: "var(--text-3)" }}>optional</span></label>
            <textarea className="textarea" style={{ minHeight: 80 }} value={form.additionalContext} onChange={set("additionalContext")} placeholder="Any constraints, existing tech stack, compliance requirements, or anything startups should know..." />
          </div>

          {error && <div className="form-error" style={{ padding: "8px 12px", background: "#fff5f5", borderRadius: "var(--radius)" }}>{error}</div>}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", borderTop: "1px solid var(--border)", paddingTop: 16 }}>
            <button type="button" className="btn btn-outline" onClick={() => navigate("/my-needs")}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting} style={{ minWidth: 140, justifyContent: "center" }}>
              {submitting ? <span className="spinner" style={{ width: 16, height: 16 }}></span> : "Post challenge →"}
            </button>
          </div>
        </div>
      </form>
      </div>
    </div>
  );
}
